import { useEffect, useState } from 'react';
import { get, useController, useFormContext } from 'react-hook-form';
import {
  Box,
  Tabs as MuiTabs,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import styled from 'styled-components';
import { HTTP_URL_PATTERN } from '../../../helpers/knowledgeHelper';
import { useTranslation } from '../../../hooks/useTranslation';
import StyledFormItem from '../../../styles/StyledFormItem';
import StyledTab from '../Tabs/StyledTab';
import DigestsConfigFields from '../DigestsConfigFields/DigestsConfigFields';
import Input from '../Input/Input';
import Switch from '../Switch/Switch';
import Text from '../Text/Text';
import ToolTip from '../ToolTip/ToolTip';
import WebscrapExcludePatterns from './WebscrapExcludePatterns';
import WebscrapLinksField from './WebscrapLinksField';
import WebscrapNumberField from './WebscrapNumberField';

// Overrides two sources of uneven spacing so every item in the section is
// separated by the same gap: labels' own margin-bottom (from StyledFormItem's
// global `label` rule) and the empty MUI FormHelperText paragraph that
// Input/Dropdown always render below themselves, which otherwise adds extra
// invisible height only after those two fields.
const FieldsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  & > label {
    margin-bottom: 0 !important;
  }

  & .MuiFormHelperText-root:empty {
    display: none;
  }

  &[hidden] {
    display: none;
  }
`;

// Soft panel that groups the website-import controls so they read as one
// settings block between the two file lists, not as orphan form fields.
const ImportPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 1.125rem;
  border: 1px solid rgba(15, 23, 42, 0.1);
  border-radius: 0.5rem;
  background-color: #f8fafc;

  & .MuiOutlinedInput-root {
    background-color: #ffffff;
    min-height: 40px;
  }

  & .MuiOutlinedInput-input {
    padding-top: 8.5px;
    padding-bottom: 8.5px;
    box-sizing: border-box;
  }
`;

// Same round-border pill styling as the shared Tabs component
// (components/shared/Tabs/Tabs.tsx), reused here directly rather than
// through that component because Tabs couples its active-tab state to the
// dashboard's URL query param and dashboard-only analytics events - neither
// of which applies to a Basic/Advanced split inside a form.
const SubTabsBar = styled(MuiTabs)`
  padding: 0.25rem;
  border-radius: 999px;
  background-color: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  min-height: 44px;

  .MuiTabs-indicator {
    display: none;
  }
`;

// Registers webscrap_enabled/webscrap_frequency/webscrap_source_url plus the
// advanced settings (sitemap/depth, page caps, exclude patterns, image
// extraction) on whatever react-hook-form context it's mounted under -
// reused wherever a knowledge base is authored (standalone KnowledgeForm,
// call agent creation/edit, chat agent creation/edit).
const WebscrapConfigFields = () => {
  const { t } = useTranslation();
  const {
    watch,
    control,
    formState: { errors },
  } = useFormContext();
  const [activeSubTab, setActiveSubTab] = useState<number>(0);
  const webscrapEnabled = watch('webscrap_enabled');
  // The backend accepts a config with no website URL as long as it has an
  // extra page or a digest to scrape instead.
  const sourceUrlRequired =
    !watch('webscrap_links')?.length && !watch('webscrap_digests')?.length;
  const sourceUrlError = get(errors, 'webscrap_source_url');
  const hasBasicErrors = !!sourceUrlError || !!get(errors, 'webscrap_links');
  const hasAdvancedErrors = !!get(errors, 'webscrap_digests');

  // Both tab panels stay mounted (the inactive one hidden) so RHF validates
  // every field, not just the visible tab's. When validation fails only on
  // the hidden tab, switch to it so the errors are visible.
  useEffect(() => {
    if (activeSubTab === 0 && !hasBasicErrors && hasAdvancedErrors) {
      setActiveSubTab(1);
    } else if (activeSubTab === 1 && !hasAdvancedErrors && hasBasicErrors) {
      setActiveSubTab(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBasicErrors, hasAdvancedErrors]);
  const { field: frequencyField } = useController({
    name: 'webscrap_frequency',
    control,
    defaultValue: 'weekly',
  });
  const webscrapFrequencyOptions = [
    { label: t('Daily'), value: 'daily' },
    { label: t('Weekly'), value: 'weekly' },
    { label: t('Monthly'), value: 'monthly' },
    { label: t('Yearly'), value: 'yearly' },
  ];

  return (
    <StyledFormItem marginBottom="1.25rem">
      <ImportPanel>
        <FieldsStack>
          <Text type="label">{t('Import from website')}</Text>
          <Switch
            name="webscrap_enabled"
            label={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.9375rem',
                  color: 'var(--primary-text-color)',
                }}
              >
                {t('Automatically refresh this knowledge base from a website')}
                <ToolTip
                  title={t(
                    'Refreshing periodically re-scrapes the website below on the schedule you choose, so this knowledge base stays up to date.'
                  )}
                />
              </Box>
            }
          />
          {webscrapEnabled && (
            <>
              {/* Basic/Advanced split so the handful of settings most people
                  need (URL, frequency, sitemap, max pages, extra pages,
                  exclude URLs) aren't buried among the technical,
                  developer-oriented ones (crawl depth, pages per PDF, image
                  extraction, digests) - reuses the app's round-border pill tab
                  styling (see Tabs/StyledTab) for a familiar, discoverable
                  switch instead of a small text disclosure link. */}
              <SubTabsBar
                value={activeSubTab}
                onChange={(_, value) => setActiveSubTab(value)}
                variant="fullWidth"
                aria-label={t('Import from website')}
              >
                <StyledTab label={t('Basic')} id="webscrap-tab-basic" />
                <StyledTab label={t('Advanced')} id="webscrap-tab-advanced" />
              </SubTabsBar>

              <FieldsStack
                hidden={activeSubTab !== 0}
                id="webscrap-basic-settings"
                aria-labelledby="webscrap-tab-basic"
                role="tabpanel"
              >
                <Text type="label">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                    }}
                  >
                    {t('Website URL')}
                    <ToolTip
                      title={t(
                        'The page the scraper starts from. If the site has a sitemap, the pages it lists are included as well.'
                      )}
                    />
                  </Box>
                </Text>
                <Input
                  name="webscrap_source_url"
                  placeholder="https://example.com"
                  size="small"
                  required={sourceUrlRequired}
                  pattern={{ value: HTTP_URL_PATTERN, message: '' }}
                  withError={!!sourceUrlError}
                  helperText={
                    sourceUrlError?.type === 'pattern'
                      ? t('Enter a full URL starting with http:// or https://')
                      : t(
                          'Enter a website URL, or add an extra page or a digest'
                        )
                  }
                />

                <Switch
                  name="webscrap_use_sitemap"
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontSize: '0.9375rem',
                      }}
                    >
                      {t("Use the site's sitemap")}
                      <ToolTip
                        title={t(
                          'When off, the scraper crawls the site from the URL above instead of using its sitemap.'
                        )}
                      />
                    </Box>
                  }
                />

                <Text type="label">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                    }}
                  >
                    {t('Scrape frequency')}
                    <ToolTip
                      title={t(
                        'How often the scraper revisits the website to refresh this knowledge base.'
                      )}
                    />
                  </Box>
                </Text>
                <ToggleButtonGroup
                  value={frequencyField.value}
                  exclusive
                  fullWidth
                  color="primary"
                  aria-label={t('Scrape frequency')}
                  onChange={(_, value) => {
                    if (value !== null) frequencyField.onChange(value);
                  }}
                  sx={{
                    p: '0.25rem',
                    borderRadius: '999px',
                    backgroundColor: '#FFFFFF',
                    border: '1px solid rgba(15, 23, 42, 0.06)',
                    gap: '0.25rem',
                    '& .MuiToggleButtonGroup-grouped': {
                      border: 0,
                      borderRadius: '999px !important',
                      margin: 0,
                    },
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontFamily: 'var(--default-fonts)',
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      color: '#64748B',
                      py: '0.45rem',
                      '&.Mui-selected': {
                        backgroundColor: '#F8FAFC',
                        color: 'var(--primary-color)',
                        '&:hover': {
                          backgroundColor: '#F1F5F9',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(15, 23, 42, 0.04)',
                      },
                    },
                  }}
                >
                  {webscrapFrequencyOptions.map(option => (
                    <ToggleButton key={option.value} value={option.value}>
                      {option.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                <WebscrapNumberField
                  name="webscrap_max_pages"
                  label={t('Max pages')}
                  tooltip={t(
                    'The most pages the scraper will collect in one run.'
                  )}
                />

                <WebscrapLinksField />

                <WebscrapExcludePatterns />
              </FieldsStack>

              <FieldsStack
                hidden={activeSubTab !== 1}
                id="webscrap-advanced-settings"
                aria-labelledby="webscrap-tab-advanced"
                role="tabpanel"
              >
                <WebscrapNumberField
                  name="webscrap_max_depth"
                  label={t('Crawl depth')}
                  tooltip={t(
                    'How many links deep the scraper follows from the starting URL when it crawls the site: always when the sitemap is off, or when the site has no sitemap.'
                  )}
                />

                <WebscrapNumberField
                  name="webscrap_pages_per_pdf"
                  label={t('Pages per PDF')}
                  tooltip={t(
                    'How many scraped pages are combined into one PDF file.'
                  )}
                />

                <Switch
                  name="webscrap_scrape_images"
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontSize: '0.9375rem',
                      }}
                    >
                      {t('Extract text from images')}
                      <ToolTip
                        title={t(
                          'Descriptions of images on the page (like text in a poster) are added to the knowledge base. The images themselves are never shown in chat.'
                        )}
                      />
                    </Box>
                  }
                />

                <DigestsConfigFields />
              </FieldsStack>
            </>
          )}
        </FieldsStack>
      </ImportPanel>
    </StyledFormItem>
  );
};

export default WebscrapConfigFields;
